import React, { useEffect, useRef } from "react";

/**
 * Ambient hero background: packets routed between nodes — the business the
 * company is actually in. Pauses entirely under prefers-reduced-motion and
 * when the tab is hidden.
 */
function NetworkFabric() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let nodes = [];
    let edges = [];
    let packets = [];
    let width = 0;
    let height = 0;
    let frame = 0;

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      if (!width || !height) return;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cols = Math.max(4, Math.round(width / 170));
      const rows = Math.max(3, Math.round(height / 150));

      nodes = [];
      for (let i = 0; i < cols; i += 1) {
        for (let j = 0; j < rows; j += 1) {
          nodes.push({
            x: ((i + 0.5) * width) / cols + Math.sin(i * 3.7 + j) * 22,
            y: ((j + 0.5) * height) / rows + Math.cos(j * 2.3 + i) * 20,
          });
        }
      }

      const reach = Math.max(width / cols, height / rows) * 1.35;
      edges = [];
      nodes.forEach((a, i) => {
        nodes.forEach((b, j) => {
          if (j > i && Math.hypot(a.x - b.x, a.y - b.y) < reach) edges.push([i, j]);
        });
      });

      packets = edges.map((_, i) => ({
        edge: i,
        t: (i * 0.137) % 1,
        speed: 0.0016 + ((i * 7) % 9) * 0.00035,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      ctx.strokeStyle = "rgba(62,110,240,0.16)";
      ctx.lineWidth = 1;
      edges.forEach(([i, j]) => {
        ctx.beginPath();
        ctx.moveTo(nodes[i].x, nodes[i].y);
        ctx.lineTo(nodes[j].x, nodes[j].y);
        ctx.stroke();
      });

      ctx.fillStyle = "rgba(157,182,247,0.45)";
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      });

      packets.forEach((p) => {
        const [i, j] = edges[p.edge];
        const a = nodes[i];
        const b = nodes[j];
        if (!a || !b) return;
        const px = a.x + (b.x - a.x) * p.t;
        const py = a.y + (b.y - a.y) * p.t;
        const g = ctx.createRadialGradient(px, py, 0, px, py, 10);
        g.addColorStop(0, "rgba(120,160,255,0.95)");
        g.addColorStop(1, "rgba(6,64,220,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, 10, 0, Math.PI * 2);
        ctx.fill();
        if (!reduce) {
          p.t += p.speed;
          if (p.t > 1) p.t = 0;
        }
      });

      if (!reduce) frame = window.requestAnimationFrame(draw);
    };

    const start = () => {
      window.cancelAnimationFrame(frame);
      build();
      draw();
    };

    const onVisibility = () => {
      if (document.hidden) {
        window.cancelAnimationFrame(frame);
      } else if (!reduce) {
        frame = window.requestAnimationFrame(draw);
      }
    };

    start();
    window.addEventListener("resize", start);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", start);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden="true" />;
}

export default NetworkFabric;
